import type { FC } from "react";
import React, { memo, useRef } from "react";
import clsx from "clsx";
import styles from "./ExteriorSlider.module.scss";
import type { ImageOptionsType } from "../../../../../../../../common-deprecated/settings/utils/ccisSettingUtils";
import { useSlider } from "../../../../../../../../common-deprecated/features/apheleia/hooks/useSlider";
import { useCarConfigSelector } from "../../../../../redux/store";
import { getCarDBAssetSrcSet } from "../../../../../../../shared-logic/utils/imageUtils";
import { useAphImageSrc } from "../../../../../hooks/useAphVisual";
import { SpinnerType } from "../../../../../redux/slices/CarConfigSpinNavigationSlice";

const ExteriorSlider: FC<{
    frames?: number[];
    imageOptions?: ImageOptionsType;
    isWheel?: boolean;
}> = ({ frames, imageOptions, isWheel }) => {
    const useAvailableInOtherGradeImages = imageOptions?.useAvailableInOtherGradeImages;

    const commonSettings = useCarConfigSelector((state) => state.commonSettings);
    const basket = useCarConfigSelector((state) => state.basket);
    const wrapperRef = useRef<HTMLDivElement | null>(null);
    const anyItemRef = useRef<HTMLImageElement | null>(null);
    const { isDragging, elementProps, currentSlide } = useSlider(wrapperRef, anyItemRef);

    const getImageSrc = useAphImageSrc({ type: "webp", bgColour: "FFFFFF", ...imageOptions });

    let totalFrames = frames?.length ?? 0;
    if (isWheel && (useAvailableInOtherGradeImages?.wheelImage || basket.wheel)) totalFrames++;
    if (isWheel && basket.winterWheel) totalFrames++;

    const exteriorSpinIsActive = useCarConfigSelector(
        (state) => state.carConfigSpinNavigation.activeSpin === SpinnerType.Car,
    );

    return (
        <div className={clsx(styles["slider-container"], exteriorSpinIsActive ? styles.active : styles.inactive)}>
            <div className={clsx(styles.slider, isDragging && styles.dragging)} ref={wrapperRef} {...elementProps}>
                {frames?.map((frame, index) => (
                    <img
                        ref={index === 0 ? anyItemRef : undefined}
                        key={frame}
                        className={styles["slider-item"]}
                        {...getImageSrc(frame)}
                        alt=""
                        loading="lazy"
                    />
                ))}
                {isWheel && basket.wheel && (
                    <div className={clsx(styles.wheel, styles["slider-item"])}>
                        <img
                            {...getCarDBAssetSrcSet(
                                commonSettings,
                                [
                                    { width: 120, height: 120 },
                                    { width: 240, height: 240 },
                                    { width: 325, height: 325 },
                                    { width: 650, height: 650 },
                                    { width: 1230, height: 1230 },
                                ],
                                useAvailableInOtherGradeImages?.wheelImage ?? basket.wheel.image,
                            )}
                            width="120"
                            height="120"
                            sizes="(max-width: 1199px) 100vw, 60vw"
                            alt={basket.wheel.name}
                            loading="lazy"
                        />
                    </div>
                )}
                {isWheel && basket.winterWheel && (
                    <div className={clsx(styles.wheel, styles["slider-item"])}>
                        <img
                            {...getCarDBAssetSrcSet(
                                commonSettings,
                                [
                                    { width: 120, height: 120 },
                                    { width: 240, height: 240 },
                                    { width: 325, height: 325 },
                                    { width: 650, height: 650 },
                                    { width: 1230, height: 1230 },
                                ],
                                basket.winterWheel.image,
                            )}
                            width="120"
                            height="120"
                            sizes="(max-width: 1199px) 100vw, 60vw"
                            alt={basket.winterWheel.name}
                            loading="lazy"
                        />
                    </div>
                )}
            </div>
            <div className={styles["slider-bullets"]} data-transform={!useAvailableInOtherGradeImages}>
                {Array(totalFrames)
                    .fill(0)
                    .map((_, index) => (
                        // This index is fixed
                        // eslint-disable-next-line react/no-array-index-key
                        <div key={index} data-active={currentSlide === index} aria-hidden="true" />
                    ))}
            </div>
        </div>
    );
};

export default memo(ExteriorSlider);
